import React from "react";
import { TableRow, TableCell } from "@mui/material";
import { BaseFormFieldProps, EditableTableRowProps, WintFormKey } from "models/propTypes/wintFormPropsTypes";
import cssExports from "theme/styles/main.module.scss";
import Icon from "../../icon";
import { IconDefinitionConstants } from "utils/constants";
import RoundButton from "../../buttons/roundButton";
import { StyleEnums } from "models/enums";
import { setIn } from "formik";
import clsx from "clsx";

function EditableTableRow<T extends Record<string, any>>(props: EditableTableRowProps<T>) {
  const {
    connect,
    name,
    index,
    row,
    fields,
    updateRow,
    removeRow,
    isLastRow,
    showRowIndex,
    hideActions,
    hideActionsCell,
  } = props;

  const rowName = `${name}[${index}]`;

  const setRowField = (name: WintFormKey<T>, value: any, shouldValidate?: boolean) => {
    const newRow = setIn(row, name, value);
    updateRow(index, newRow, shouldValidate);
  };

  const handleChange = (name: WintFormKey<T>) => (e: React.ChangeEvent<any>) => {
    setRowField(name, e.target.value);
  };

  const connectRowToForm = (name: WintFormKey<T>): BaseFormFieldProps => {
    const connector = connect((rowName + "." + name) as WintFormKey<any>);

    return {
      name: connector.name,
      value: connector.value,
      onChange: handleChange(name),
      onBlur: (e: React.FocusEvent<any>) => {
        connector.onBlur(e);
        connector.validate();
      },
      setValue: (value: any, shouldValidate?: boolean) => setRowField(name, value, shouldValidate),
      setTouched: (isTouched?: boolean, shouldValidate?: boolean) => connector.setTouched(isTouched, shouldValidate),
      touched: connector.touched,
      error: connector.error,
      hasError: connector.hasError,
    };
  };

  const removeRowEvent = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    removeRow(index);
  };

  const onRowClick = React.useCallback(() => {
    if (isLastRow && !hideActions) {
      updateRow(index, { ...row }, index === 0);
    }
  }, [isLastRow, hideActions, index, row, updateRow]);

  return (
    <TableRow
      className={clsx(cssExports.fieldRow, isLastRow && !hideActions ? cssExports.tableLastRow : "")}
      onClick={onRowClick}
    >
      {showRowIndex ? <TableCell>{isLastRow && !hideActions ? "" : index + 1}</TableCell> : <></>}
      {Object.keys(fields).map((k) => {
        const field = fields[k]!;
        const widthPercentage =
          field.widthColumnCount && typeof field.widthColumnCount === "number"
            ? `${(field.widthColumnCount / 12) * 100}%`
            : undefined;
        return (
          <TableCell key={`table-cell-${row[props.idField!] ?? index}-${k}`} style={{ width: widthPercentage }}>
            {React.createElement(field.Component, {
              ...connectRowToForm(k as WintFormKey<T>),
              ...(field.extraProps ?? {}),
            })}
          </TableCell>
        );
      })}
      {!hideActionsCell && (
        <TableCell className={cssExports.editableTableActionButton}>
          {!isLastRow && !hideActions ? (
            <RoundButton
              size="xs"
              displayType={StyleEnums.DisplayType.None}
              className={cssExports.close}
              onClick={removeRowEvent}
            >
              <Icon icon={IconDefinitionConstants.WTimes} />
            </RoundButton>
          ) : (
            <></>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

export default EditableTableRow;
